"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const joi_1 = __importDefault(require("joi"));
const errors_1 = require("../../errors");
const ContactInfoValidator_1 = __importDefault(require("../../shared/validators/ContactInfoValidator"));
const PayrixMerchantStatus_1 = require("../models/appOrganization/PayrixMerchantStatus");
const BillingAddressValidator_1 = __importDefault(require("./BillingAddressValidator"));
const GenericValidator_1 = __importDefault(require("./GenericValidator"));
const InvoiceNumberSettingsValidator_1 = __importDefault(require("./InvoiceNumberSettingsValidator"));
const notificationConfiguration_schema_1 = __importDefault(require("./schemas/notificationConfiguration.schema"));
class AppBillingOrganizationValidator extends GenericValidator_1.default {
    constructor(invoiceNumberSetingsValidator, billingAddressValidator, contactValidator) {
        super();
        this._invoiceNumberSetingsValidator =
            invoiceNumberSetingsValidator ??
                new InvoiceNumberSettingsValidator_1.default();
        this._billingAddressValidator =
            billingAddressValidator ?? new BillingAddressValidator_1.default();
        this._contactValidator = contactValidator ?? new ContactInfoValidator_1.default();
    }
    _invoiceNumberSetingsValidator;
    _billingAddressValidator;
    _contactValidator;
    payrixMerchantStatusOptions = Object.values(PayrixMerchantStatus_1.PayrixMerchantStatus);
    _convenienceFeesSettingsSchema = joi_1.default.object({
        CCFeeEnabled: joi_1.default.boolean().required(),
        CCFee: joi_1.default.when('CCFeeEnabled', [
            {
                is: true,
                then: joi_1.default.number().min(0).max(4).precision(2).required(),
                otherwise: joi_1.default.number().optional(),
            },
        ]),
        ACHFeeEnabled: joi_1.default.boolean().required(),
        ACHFee: joi_1.default.when('ACHFeeEnabled', [
            {
                is: true,
                then: joi_1.default.number().min(0).max(2).precision(2).required(),
                otherwise: joi_1.default.number().optional(),
            },
        ]),
        ACHMaximumFee: joi_1.default.when('ACHFeeEnabled', [
            {
                is: true,
                then: joi_1.default.number().min(0).max(100).required(),
                otherwise: joi_1.default.number().optional(),
            },
        ]),
    });
    _notificationSettingsSchema = joi_1.default.object({
        sendersName: joi_1.default.string().required(),
        notificationConfigurations: joi_1.default.array()
            .items(notificationConfiguration_schema_1.default)
            .required(),
    });
    _schema = joi_1.default.object({
        payrixMerchantId: joi_1.default.string().optional(),
        payrixApiKey: joi_1.default.string().optional(),
        payrixProducts: joi_1.default.object()
            .pattern(joi_1.default.string(), joi_1.default.string())
            .optional(),
        payrixLoginId: joi_1.default.string().optional(),
        invoiceNumberSettings: joi_1.default.object().optional(),
        class: joi_1.default.array().items(joi_1.default.string()).max(10).unique().optional(),
        payrixMerchantStatus: joi_1.default.string()
            .optional()
            .valid(...this.payrixMerchantStatusOptions),
        contact: joi_1.default.object().optional(),
        address: joi_1.default.object().optional(),
        logo: joi_1.default.string().optional(),
        lastRequestedWmsEventId: joi_1.default.number().optional(),
        lastFetchTimestamp: joi_1.default.number().optional(),
        convenienceFeesSettings: this._convenienceFeesSettingsSchema.optional(),
        notificationSettings: this._notificationSettingsSchema.optional(),
        canCustomerDownloadInvoiceDetails: joi_1.default.bool().optional(),
    }).required();
    validate(appBillingOrganization) {
        super.validate(appBillingOrganization, 'IAppBillingOrganization', this._schema);
        const issues = [];
        const invoiceNumberSettings = appBillingOrganization?.invoiceNumberSettings;
        const address = appBillingOrganization?.address;
        const contact = appBillingOrganization?.contact;
        if (invoiceNumberSettings) {
            try {
                this._invoiceNumberSetingsValidator.validate(invoiceNumberSettings);
            }
            catch (error) {
                if (error instanceof errors_1.ValidationError) {
                    issues.push(...error.validationIssues);
                }
                else {
                    issues.push(new errors_1.Errors.ValidationIssue('IAppBillingOrganization', errors_1.Errors.ValidationErrorCode.invalidValue, 'invoiceNumberSettings', invoiceNumberSettings));
                }
            }
        }
        if (address) {
            try {
                this._billingAddressValidator.validate(address);
            }
            catch (error) {
                if (error instanceof errors_1.ValidationError) {
                    issues.push(...error.validationIssues);
                }
                else {
                    issues.push(new errors_1.Errors.ValidationIssue('IAppBillingOrganization', errors_1.Errors.ValidationErrorCode.invalidValue, 'address', address));
                }
            }
        }
        if (contact) {
            try {
                this._contactValidator.validate(contact);
            }
            catch (error) {
                if (error instanceof errors_1.ValidationError) {
                    issues.push(...error.validationIssues);
                }
                else {
                    issues.push(new errors_1.Errors.ValidationIssue('IAppBillingOrganization', errors_1.Errors.ValidationErrorCode.invalidValue, 'contact', contact));
                }
            }
        }
        if (issues.length > 0) {
            const appBillingOrganizationError = new errors_1.Errors.ValidationError();
            issues.forEach((issue) => {
                appBillingOrganizationError.addIssue(issue.modelType, issue.errorCode, issue.fieldName, issue.value, issue.path);
            });
            throw appBillingOrganizationError;
        }
    }
}
exports.default = AppBillingOrganizationValidator;
